import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Product from 'services/Product'
import Select from 'components/Inputs/Select'
import TextInput from 'components/Inputs/TextInput'
import BusinessProductList from 'components/Lists/BusinessProductList'
import BusinessProductsModal from 'pages/Modals/BusinessProductsModal'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import AddProductModal from 'pages/Modals/AddProductModal'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  Container,
  Row,
  InfoText,
  LinkText
} from '../styles'

export default function EditProducts () {
  const [loadingData, setLoadingData] = useState (true)
  const [loadingProducts, setLoadingProducts] = useState (true)
  const [products, setProducts] = useState ([])
  const [categories, setCategories] = useState ([])
  const [category, setCategory] = useState (null)
  const [name, setName] = useState ('')
  const [useInService, setUseInService] = useState (null)
  const [showProductsModal, setShowProductsModal] = useState (false)
  const [showAddProductModal, setShowAddProductModal] = useState (false)
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const location = useLocation ()
  const isTutorial = !!location.state?.isTutorial

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [])

  useEffect (() => {
    getProducts ()
  }, [name, category, useInService])

  const getData = async () => {
    try {
      setLoadingData (true)

      const { data: categories } = await cutileiApi.get ('/product_categories', requestConfig)

      setCategories (categories.map (category => ({
        label: category.name,
        value: category.id
      })))
    } catch (error) {
      logApiError (error)
    } finally {
      setLoadingData (false)
    }
  }

  const getProducts = async () => {
    let filters = 'status=active'
    if (name) filters += `&name=${encodeURIComponent (name)}`
    if (category) filters += `&category_id=${category.value}`
    if (!!useInService) filters += `&used_in_service=${useInService.value}`
    
    try {
      setLoadingProducts (true)

      const { data: products } = await cutileiApi.get (
        `/businesses/${businessId}/products?${filters}`, requestConfig
      )

      setProducts (products)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoadingProducts (false)
    }
  }

  const compareProducts = (a, b) => (
    DateTime.fromISO (a.created_at).toMillis () - DateTime.fromISO (b.created_at).toMillis ()
  )

  const toggleProductsModal = () => setShowProductsModal (!showProductsModal)
  const toggleAddProductModal = () => setShowAddProductModal (!showAddProductModal)

  return (
    <Container>
      {isTutorial && (
        <InfoText style={{marginTop: -5, marginBottom: 15}}>
          <LinkText to='/business'>
            {'Clique aqui '}
          </LinkText>
          para voltar para o <b>guia de configuração do salão</b>
        </InfoText>
      )}
      <Row style={{gap: 10, width: 740}}>
        <Select
          name='category'
          placeholder='Categoria...'
          value={category}
          options={categories}
          isClearable={true}
          onChange={(_, option) => setCategory (option)}
        />
        <Select
          placeholder='Finalidade...'
          value={useInService}
          options={Product.USE_OPTIONS}
          isClearable={true}
          onChange={(_, option) => setUseInService (option)}
        />
        <TextInput
          value={name}
          placeholder='Pesquisar por nome'
          isClearable={true}
          onChange={e => setName (e.target.value)}
          onClear={() => setName ('')}
          inputStyle={{boxShadow: '0px 1px 10px -6px', paddingInline: 12}}
        />
      </Row>
      {AuthService.hasPermission ('business_config.products', 'edit') && (
        <Row style={{gap: 10, marginBottom: 20}}>
          <SecondaryButton
            label='Adicionar produtos'
            onClick={toggleProductsModal}
            width={170}
          />
          <SecondaryButton
            label='Cadastrar produto'
            onClick={toggleAddProductModal}
            width={170}
          />
          <BusinessProductsModal
            visible={showProductsModal}
            onConfirm={addedProducts => setProducts (
              [...products, ...addedProducts].sort (compareProducts)
            )}
            onClose={toggleProductsModal}
          />
          <AddProductModal
            visible={showAddProductModal}
            productCategories={categories}
            onConfirm={product => setProducts ([product, ...products])}
            onClose={toggleAddProductModal}
          />
        </Row>
      )}
      {loadingData || loadingProducts ? <Loading/> : (
        <BusinessProductList
          products={products}
          onEdit={product => setProducts (products.map (p => p.id === product.id ? {...product} : p))}
          onDelete={deletedProduct => setProducts (products.filter (p => p.id !== deletedProduct.id))}
        />
      )}
    </Container>
  )
}
